import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import usePrivilege from "../../hooks/usePrivilege";
import { useUser } from "../../context/UserContext";
import axiosInstance from "../../services/api/axios";
import { getMediaUrl } from "../../utils/media";
import {
  editProject,
  getProjectDeatilaFormdata,
  fetchProjectPhaseMedia,
} from "../../services/projects";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import { Button, Chip, Dialog, IconButton, Tabs, Tooltip } from "@material-tailwind/react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Delete, Edit, Eye, PackagePlus, Pencil, Plus, Trash2 } from "lucide-react";
import { DeleteButton, EditButton, OutlineButton, PrimaryButton, SecondaryButton } from "../Buttons";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

import { convertFilesToBase64 } from "../../utils/fileUtils"; // Import Here
import { mergeProjectPhasesWithServer, normalizeProjectPhasePayload } from "../../utils/common";
import { pascalToSentenceCase } from "../../utils/helpers";
import AmenitiesSelect from "../ProjectForms/AmenitiesSelect";
import ConfirmationModal from "../ConfirmationModal";
import { useNavigate } from "react-router-dom";
import PhasesList from "./PhasesList";

/** Nested `address` or flat `address_*` fields from API — aligned with ProjectDetailsForm. */
function normalizeProjectAddressFromApi(data) {
  if (!data) {
    return {
      buildingNo: "",
      streetName1: "",
      streetName2: "",
      city: "",
      district: "",
      state: "",
      pinCode: "",
      country: "",
    };
  }
  const a = data.address;
  return {
    buildingNo: (a?.buildingNo ?? data.address_building_no) || "",
    streetName1: (a?.streetName1 ?? data.address_street_name1) || "",
    streetName2: (a?.streetName2 ?? data.address_street_name2) || "",
    city: (a?.city ?? data.address_city) || "",
    district: (a?.district ?? data.address_district) || "",
    state: (a?.state ?? data.address_state) || "",
    pinCode: (a?.pinCode ?? data.address_pin_code) || "",
    country: (a?.country ?? data.address_country) || "",
  };
}

function ProjectDetailsContent({
  project: propProject,
  projectId,
  projectPhases,
}) {
  const navigate = useNavigate();

  const [project, setProject] = useState(null);
  const [, setError] = useState(null);
  const { userInfo } = useUser();
  const [mediaImages, setMediaImages] = useState([]);

  const { hasPrivilege } = usePrivilege();
  const [imageLoading, setImageLoading] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [editableProject, setEditableProject] = useState({});
  const [initialProjectData, setInitialProjectData] = useState({});
  const [, setParsedBankDetails] = useState({});
  const [newImages, setNewImages] = useState([]);
  const [previewNewImages, setPreviewNewImages] = useState([]);
  const imageInputRef = useRef(null);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [editableBankDetails] = useState({});
  const [, setImagesToDelete] = useState([]);
  const [editableCostSheetTerms, setEditableCostSheetTerms] = useState("");
  const [fetchingImages, setFetchingImages] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [, setIsEditingAmenities] = useState(false);
  const [isProjectDeleteConfirmationOpen, setIsProjectDeleteConfirmationOpen] = useState(false); // State for the project delete confirmation modal
  const [localProjectPhases, setLocalProjectPhases] = useState(projectPhases || []);
  const [, setPhaseMedia] = useState({}); // Store media for each phase: { phaseId: { images: [], guidelines: [], boq: [] } }

  const fetchProjectData = useCallback(async () => {
    try {
      setFetchingImages(true);
      const response = await axiosInstance.get(`/projects`, {
        params: {
          projectId: projectId,
          customerId: userInfo?.loginIdentifierDetails?.customer?.customerId,
        },
      });
      
      // Handle paginated response format: { items: [...], total: 1, page: 1, ... }
      let projectData = null;
      if (response?.items && Array.isArray(response.items) && response.items.length > 0) {
        projectData = response.items[0];
      } else if (Array.isArray(response) && response.length > 0) {
        projectData = response[0];
      }
      
      if (projectData) {
        const addr = normalizeProjectAddressFromApi(projectData);
        if (process.env.NODE_ENV === "development") {
          console.log("[ProjectDetailsContent] address debug", {
            nestedAddress: projectData.address,
            flatKeys: {
              address_building_no: projectData.address_building_no,
              address_street_name1: projectData.address_street_name1,
              address_city: projectData.address_city,
            },
            normalized: addr,
          });
        }
        setProject(projectData);
        setInitialProjectData(projectData);
        setEditableProject({
          projectId: projectData.projectId,
          projectName: projectData.projectName || "",
          developer: projectData.developer || "",

          ...addr,

          reraNo: projectData.reraNo || "",
          towers: projectData.towers || "",
          sizeAcres: projectData.sizeAcres || "",
          units: projectData.units || "",
          phoneNumber: projectData.phoneNumber || projectData.phone_number || "",
          whatsappNumber: projectData.whatsappNumber || projectData.whatsapp_number || "",
          specifications: projectData.specifications || "",
          location: projectData.location || "",
          costSheetTerms: projectData.costSheetTerms || "",
          reraEndDate: projectData.reraEndDate || "",
          amenities: projectData.amenities ? projectData.amenities.split(',') : [], // Ensure amenities is always an array
        });
        setEditableCostSheetTerms(projectData.costSheetTerms || "");
      } else {
        setProject(null);
      }

      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch project data");
      console.error("Error fetching project:", err);
    } finally {
      setFetchingImages(false);
    }
  }, [projectId, userInfo]);

  useEffect(() => {
    fetchProjectData();
  }, [fetchProjectData]);

  // Function to fetch phase media
  const fetchPhaseMedia = async (phaseId) => {
    if (!phaseId) return;
    
    try {

      const mediaData = await fetchProjectPhaseMedia(phaseId, {
        images: true,
        progress: false,
        guidelines: true,
        boq: true,
      });
      
   
      
      // Organize media by type
      const organizedMedia = {
        images: [],
        guidelines: [],
        boq: [],
      };
      
      // Handle different response formats
      const mediaArray = Array.isArray(mediaData) ? mediaData : (mediaData?.items || []);
      
      mediaArray.forEach((item) => {
        // Check various possible field names for media type
        const mediaType = item.mediaType || item.type || item.media_type || item.category;
        
        if (mediaType === 'images' || mediaType === 'image' || item.isImage) {
          organizedMedia.images.push(item);
        } else if (mediaType === 'guidelines' || mediaType === 'guideline' || mediaType === 'interior_design_guidelines' || item.isGuidelines) {
          organizedMedia.guidelines.push(item);
        } else if (mediaType === 'boq' || mediaType === 'rera_boq_document' || mediaType === 'rera_boq' || item.isBoq) {
          organizedMedia.boq.push(item);
        } else {
          // If no type specified, check if it's a document (PDF, DOC, etc.)
          const contentType = item.contentType || item.content_type || item.type;
          if (contentType && (contentType.includes('pdf') || contentType.includes('doc') || contentType.includes('word'))) {
            // Try to determine type from filename or key
            const fileName = item.name || item.key || '';
            if (fileName.toLowerCase().includes('guideline') || fileName.toLowerCase().includes('design')) {
              organizedMedia.guidelines.push(item);
            } else if (fileName.toLowerCase().includes('boq') || fileName.toLowerCase().includes('rera')) {
              organizedMedia.boq.push(item);
            }
          }
        }
      });
      

      
      setPhaseMedia((prev) => ({
        ...prev,
        [phaseId]: organizedMedia,
      }));
    } catch (error) {
      console.error(`Error fetching media for phase ${phaseId}:`, error);
    }
  };

  // Update local phases when projectPhases prop changes (merge so optimistic creates are not wiped)
  useEffect(() => {
    if (projectPhases) {
      setLocalProjectPhases((prev) =>
        mergeProjectPhasesWithServer(projectPhases, prev)
      );
      projectPhases.forEach((phase) => {
        const phaseId = phase.projectPhaseId || phase.project_phase_id || phase.id;
        if (phaseId) {
          fetchPhaseMedia(phaseId);
        }
      });
    }
  }, [projectPhases]);

  // Fetch images when project changes
  useEffect(() => {
    const fetchImages = async () => {
      try {
        if (project?.projectId) {
          const response = await axiosInstance.get("/projects/media", {
            params: {
              project_id: project.projectId,
              images: true,
            },
          });
          setMediaImages(response);
        }
      } catch (error) {
        if (
          error.response &&
          (error.response.status === 404 ||
            (error.response.data &&
              error.response.data.detail ===
              "No Project Media objects found"))
        ) {
          setMediaImages([]);
        } else {
          console.error("Error fetching media images:", error);
        }
      } finally {
        setFetchingImages(false);
      }
    };

    if (project && project.projectId) {
      setFetchingImages(true);
      fetchImages();
    }
  }, [project]);

  const handleEditClick = (e) => {
    e.stopPropagation();
    setIsEditing(true);
    setIsEditingAmenities(true);
    // Keep editable shape consistent with fetchProjectData() (flat address fields).
    const addr = normalizeProjectAddressFromApi(initialProjectData);
    setEditableProject({
      projectId: initialProjectData.projectId,
      projectName: initialProjectData.projectName || "",
      developer: initialProjectData.developer || "",
      ...addr,
      reraNo: initialProjectData.reraNo || "",
      towers: initialProjectData.towers || "",
      sizeAcres: initialProjectData.sizeAcres || "",
      units: initialProjectData.units || "",
      phoneNumber: initialProjectData.phoneNumber || initialProjectData.phone_number || "",
      whatsappNumber: initialProjectData.whatsappNumber || initialProjectData.whatsapp_number || "",
      specifications: initialProjectData.specifications || "",
      location: initialProjectData.location || "",
      costSheetTerms: initialProjectData.costSheetTerms || "",
      reraEndDate: initialProjectData.reraEndDate || "",
      amenities: initialProjectData.amenities
        ? initialProjectData.amenities.split(",")
        : [],
    });
  };

  const handleInputChange = (e) => {
    e.stopPropagation();
    const { name, value } = e.target;
    setEditableProject((prev) => ({ ...prev, [name]: value }));
  };

  const handleCancelEdit = (e) => {
    e.stopPropagation();
    setIsEditing(false);
    setIsEditingAmenities(false);
    setNewImages([]);
    setPreviewNewImages([]);
    setImagesToDelete([]);
    const addr = normalizeProjectAddressFromApi(initialProjectData);
    setEditableProject({
      projectId: initialProjectData.projectId,
      projectName: initialProjectData.projectName || "",
      developer: initialProjectData.developer || "",
      ...addr,
      reraNo: initialProjectData.reraNo || "",
      towers: initialProjectData.towers || "",
      sizeAcres: initialProjectData.sizeAcres || "",
      units: initialProjectData.units || "",
      phoneNumber: initialProjectData.phoneNumber || initialProjectData.phone_number || "",
      whatsappNumber: initialProjectData.whatsappNumber || initialProjectData.whatsapp_number || "",
      specifications: initialProjectData.specifications || "",
      location: initialProjectData.location || "",
      costSheetTerms: initialProjectData.costSheetTerms || "",
      reraEndDate: initialProjectData.reraEndDate || "",
      amenities: initialProjectData.amenities
        ? initialProjectData.amenities.split(",")
        : [],
    });
  };

  const handleImageChange = async (e) => {
    e.stopPropagation();
    const files = Array.from(e.target.files);
    const base64Images = await convertFilesToBase64(files);
    const newImageArray = files.map((file, index) => ({
      file: file,
      base64: base64Images[index],  
    }));
  
    setNewImages((prev) => [...prev, ...newImageArray]);
    setPreviewNewImages((prev) => [...prev, ...base64Images]);
  };

  const handleNewImageRemove = (index) => {
    const updatedImages = [...newImages];
    const newPreviews = [...previewNewImages];
    URL.revokeObjectURL(previewNewImages[index]);
    updatedImages.splice(index, 1);
    newPreviews.splice(index, 1);
    setNewImages(updatedImages);
    setPreviewNewImages(newPreviews);
  };

  const handleSaveClick = useCallback(
    async (e) => {
      e.stopPropagation();
      setIsEditing(false);
      setUploadingImages(true);
      try {
        const formData = getProjectDeatilaFormdata({
          ...editableProject,
          customer_id: userInfo?.loginIdentifierDetails?.customer?.customerId,
          amenities: Array.isArray(editableProject?.amenities) ? editableProject?.amenities.join(",") : editableProject?.amenities || "",
        });

        if (newImages.length > 0) {
          newImages.forEach((image, index) => {
            formData.append(`images`, image.file, `image-${index}.png`);
          });
        }

        const response = await editProject(
          editableProject.projectId,
          formData,
          userInfo?.loginId
        );
        if (response.status === 422) {
          toast.error(
            "Invalid or missing required fields. Please check your input."
          );
        } else {
          const response = await axiosInstance.get("/projects/media", {
            params: {
              project_id: project.projectId,
              images: true,
            },
          });
          setMediaImages(response);
          setNewImages([]);
          setPreviewNewImages([]);
          setParsedBankDetails(editableBankDetails);
          await fetchProjectData();
          toast.success("Project details saved");
        }
      } catch (error) {
        toast.error("Error saving Project details");
        console.error("Error in editing the project:", error);
        throw error;
      } finally {
        setUploadingImages(false);
      }
    },
    [
      editableProject,
      newImages,
      project,
      editableBankDetails,
      userInfo,
      fetchProjectData,
    ]
  );

  const handleImageLoad = (index) => {
    setImageLoading((prev) => ({ ...prev, [index]: false }));
  };

  const handleImageError = (index) => {
    setImageLoading((prev) => ({ ...prev, [index]: false }));
  };

  const inputStyle = {
    border: "1px solid #e2e8f0",
    borderRadius: "0.375rem",
    padding: "0.5rem 0.75rem",
    fontSize: "0.875rem",
    width: "100%",
    outline: "none",
    transition: "border-color 0.15s ease-in-out",
    ":focus": {
      borderColor: "#90cdf4",
      boxShadow: "0 0 0 0.2rem rgba(144, 205, 244, 0.5)",
    },
  };

  const handlePrevImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex > 0
        ? prevIndex - 1
        : isEditing
          ? previewNewImages.length - 1
          : mediaImages.length - 1
    );
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prevIndex) =>
      isEditing
        ? prevIndex < previewNewImages.length - 1
          ? prevIndex + 1
          : 0
        : prevIndex < mediaImages.length - 1
          ? prevIndex + 1
          : 0
    );
  };

  const handleOpenProjectDeleteConfirmation = () => {
    setIsProjectDeleteConfirmationOpen(true);
  };

  const handleCloseProjectDeleteConfirmation = () => {
    setIsProjectDeleteConfirmationOpen(false);
  };

  const handleConfirmProjectDelete = async () => {
    try {
      await axiosInstance.delete(`/projects`, {
        params: {
          project_id: projectId
        }
      });

      toast.success("Project deleted successfully!");
      navigate("/projects");
    } catch (error) {
      console.error("Error deleting project:", error);
      toast.error("Failed to delete project.");
    } finally {
      setIsProjectDeleteConfirmationOpen(false);
    }
  };

  const renderImageCarousel = () => {
    if (fetchingImages) {
      return (
        <div className="relative flex justify-center items-center p-8 px-6">
          <Skeleton height={350} width={350} />
        </div>
      );
    }
    if (isEditing && previewNewImages.length > 0) {
      return (
        <div className="relative flex justify-center items-center p-8 px-6">
          <img
            src={previewNewImages[currentImageIndex]?.base64}
            alt={`Preview ${currentImageIndex + 1}`}
            className={`w-36 h-36 object-cover rounded-lg shadow-lg transition-opacity duration-300 opacity-100`}
            style={{ aspectRatio: "1 / 1" }}
          />
          <div className="absolute top-1/2 transform -translate-y-1/2 flex justify-between w-full px-2 z-20">
            <button
              onClick={handlePrevImage}
              className="text-gray-600 hover:text-gray-900"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                className="w-6 h-6"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15.75 19.5L8.25 12l7.5-7.5"
                />
              </svg>
            </button>
            <button
              onClick={handleNextImage}
              className="text-gray-600 hover:text-gray-900"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                className="w-6 h-6"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M8.25 4.5l7.5 7.5-7.5 7.5"
                />
              </svg>
            </button>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleNewImageRemove(currentImageIndex);
            }}
            className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs"
          >
            ×
          </button>
        </div>
      );
    }

    if (mediaImages.length > 0) {
      return (
        <div className="relative flex justify-center items-center p-8 px-6">
          {/* Previous Image (Stacked) */}
          {mediaImages.length > 1 && (
            <div className="absolute transform -translate-x-40 scale-95 opacity-10 z-0">
              <img
                src={getMediaUrl(
                  mediaImages[
                    (currentImageIndex - 1 + mediaImages.length) %
                    mediaImages.length
                  ]
                )}
                alt="Previous"
                className="w-[350px] h-[350px] object-cover rounded-lg shadow-lg"
                style={{ aspectRatio: "1 / 1" }}
              />
            </div>
          )}

          {/* Current Image */}
          {mediaImages.length > 0 && (
            <div className="relative z-10 transition-all">
              <Dialog size="xl">
                <Dialog.Trigger>
                  <img
                    src={getMediaUrl(mediaImages[currentImageIndex])}
                    alt={`Progress ${currentImageIndex + 1}`}
                    className={`w-[350px] h-[350px] object-cover rounded-lg shadow-lg transition-all duration-300 ${imageLoading[currentImageIndex] === false
                      ? "opacity-100"
                      : "opacity-0"
                      }`}
                    onLoad={() => handleImageLoad(currentImageIndex)}
                    onError={() => handleImageError(currentImageIndex)}
                    style={{ aspectRatio: "1 / 1" }}
                  />
                </Dialog.Trigger>
                <Dialog.Overlay>
                  <Dialog.Content>
                    <div className="">
                      <img
                        alt="nature"
                        src={getMediaUrl(mediaImages[currentImageIndex])}
                        className="h-[48rem] w-full rounded-lg object-contain object-center"
                      />
                    </div>
                  </Dialog.Content>
                </Dialog.Overlay>
              </Dialog>
            </div>
          )}

          {/* Next Image (Stacked) */}
          {mediaImages.length > 1 && (
            <div className="absolute transform translate-x-40 scale-95 opacity-10 z-0">
              <img
                src={getMediaUrl(
                  mediaImages[(currentImageIndex + 1) % mediaImages.length]
                )}
                alt="Next"
                className="w-[350px] h-[350px] object-cover rounded-lg shadow-lg"
                style={{ aspectRatio: "1 / 1" }}
              />
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="absolute top-1/2 transform -translate-y-1/2 flex justify-between w-full px-2 z-20">
            <button
              onClick={handlePrevImage}
              className="bg-white/80 hover:bg-white text-gray-600 hover:text-gray-900 rounded-full p-2 transition-colors duration-200 shadow-lg"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                className="w-6 h-6"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15.75 19.5L8.25 12l7.5-7.5"
                />
              </svg>
            </button>
            <button
              onClick={handleNextImage}
              className="bg-white/80 hover:bg-white text-gray-600 hover:text-gray-900 rounded-full p-2 transition-colors duration-200 shadow-lg"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                className="w-6 h-6"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M8.25 4.5l7.5 7.5-7.5 7.5"
                />
              </svg>
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="flex justify-center items-center p-8">
        <p className="text-gray-500">No Images Available</p>
      </div>
    );
  };
  return (
    <>
      <div className="flex flex-col xl:flex-row gap-4 h-full overflow-hidden" >
      
     
        <div className="flex-1 space-y-6 h-full overflow-y-auto" >
          {/* Project details card */}

          <div className="rounded-2xl bg-white shadow-sm border border-slate-200 p-5 lg:p-6" >
            <div className="flex justify-between items-center mb-4">
              <h1 className="text-base lg:text-lg font-semibold text-slate-900">
                Project Details
              </h1>
              <div className="flex gap-2">
                {hasPrivilege("canEdit") ? (
                  isEditing ? (
                    <div className="flex gap-2">
                      <OutlineButton
                        onClick={handleCancelEdit}
                        className={"w-32"}
                      >
                        Cancel
                      </OutlineButton>
                      <PrimaryButton
                        className={"w-32"}
                        onClick={handleSaveClick}
                      >
                        {uploadingImages ? "Saving..." : "Save"}
                      </PrimaryButton>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <EditButton onClick={handleEditClick} />
                      <DeleteButton onClick={handleOpenProjectDeleteConfirmation} />
                      {/* <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={handleEditClick}
                                          color="black"
                                          className="p-2 "
                                        >
                                          <Edit size={17.5} />
                                        </Button> */}

                      {/* <Button
                                            size="sm"
                                            variant="outline"
                                            color="red"
                                            onClick={handleOpenProjectDeleteConfirmation}
                                            className="px-2 py-1 bg-red-500 text-white hover:bg-red-600 border-none p-2"
                                          >
                                            <Trash2 size={17.5} />
                                          </Button> */}
                    </div>
                  )
                ) : null}
              </div>
            </div>
            {project ? (
              <>
                <div className="overflow-hidden">
                  {renderImageCarousel()}
                  {isEditing && (
                    <div className="relative flex items-center justify-center w-full h-16  px-2 transition bg-white border-2 border-gray-300 border-dashed rounded-md appearance-none cursor-pointer hover:border-gray-400 focus:outline-none">
                      <input
                        ref={imageInputRef}
                        type="file"
                        multiple
                        onChange={handleImageChange}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        style={{ width: "100%", height: "100%" }}
                      />
                      <span className="flex items-center space-x-2">
                        <svg xmlns="http://www.w3.org/2000/svg"
                          className="w-4 h-4 text-gray-600"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                          />
                        </svg>
                        <span className="font-medium text-gray-600 text-sm">
                          Drop files to Attach, or browse
                        </span>
                      </span>
                    </div>
                  )}
                </div>
                <h2 className="text-lg font-semibold mb-4">
                  {project?.projectName}
                </h2>
                <div className="text-sm text-gray-600 space-y-4">
                  <div className="flex flex-col lg:flex-row gap-6">
                    {/* Left Column */}
                    <div className="flex-1">
                      <div className=" p-4  space-y-4">
                        <div className="flex gap-6">

                          <div className="flex-1">
                            <label className="font-medium text-gray-700">
                              Address:
                            </label>
                            {isEditing ? (
                              <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mt-2">
                                <div>
                                  <label className="block text-xs text-gray-500 mb-1">Building No</label>
                                  <input
                                    type="text"
                                    style={inputStyle}
                                    value={editableProject.buildingNo || ""}
                                    onChange={handleInputChange}
                                    name="buildingNo"
                                    placeholder="Building No"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs text-gray-500 mb-1">Street Name 1</label>
                                  <input
                                    type="text"
                                    style={inputStyle}
                                    value={editableProject.streetName1 || ""}
                                    onChange={handleInputChange}
                                    name="streetName1"
                                    placeholder="Street Name 1"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs text-gray-500 mb-1">Street Name 2</label>
                                  <input
                                    type="text"
                                    style={inputStyle}
                                    value={editableProject.streetName2 || ""}
                                    onChange={handleInputChange}
                                    name="streetName2"
                                    placeholder="Street Name 2"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs text-gray-500 mb-1">City</label>
                                  <input
                                    type="text"
                                    style={inputStyle}
                                    value={editableProject.city || ""}
                                    onChange={handleInputChange}
                                    name="city"
                                    placeholder="City"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs text-gray-500 mb-1">District</label>
                                  <input
                                    type="text"
                                    style={inputStyle}
                                    value={editableProject.district || ""}
                                    onChange={handleInputChange}
                                    name="district"
                                    placeholder="District"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs text-gray-500 mb-1">State</label>
                                  <input
                                    type="text"
                                    style={inputStyle}
                                    value={editableProject.state || ""}
                                    onChange={handleInputChange}
                                    name="state"
                                    placeholder="State"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs text-gray-500 mb-1">Pincode</label>
                                  <input
                                    type="text"
                                    style={inputStyle}
                                    value={editableProject.pinCode || ""}
                                    onChange={handleInputChange}
                                    name="pinCode"
                                    placeholder="Pincode"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs text-gray-500 mb-1">Country</label>
                                  <input
                                    type="text"
                                    style={inputStyle}
                                    value={editableProject.country || ""}
                                    onChange={handleInputChange}
                                    name="country"
                                    placeholder="Country"
                                  />
                                </div>
                              </div>
                            ) : (() => {
                              const addressLine = [
                                editableProject.buildingNo,
                                editableProject.streetName1,
                                editableProject.streetName2,
                                editableProject.city,
                                editableProject.district,
                                editableProject.state,
                                editableProject.pinCode,
                                editableProject.country,
                              ]
                                .filter(Boolean)
                                .join(", ");
                              return addressLine ? (
                                <p>{addressLine}</p>
                              ) : (
                                <p>No address available</p>
                              );
                            })()}
                          </div>

                        </div>
                        <div className="flex gap-6">
                          
                          <div className="flex-1">
                            <label className="font-medium text-gray-700">
                              Location:
                            </label>
                            {isEditing ? (
                              <input
                                type="text"
                                style={inputStyle}
                                value={editableProject.location || ""}
                                onChange={handleInputChange}
                                name="location"
                              />
                            ) : (
                              <p>{editableProject.location}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-6">
                          <div className="flex-1">
                            <label className="font-medium text-gray-700">
                              Developer:
                            </label>
                            {isEditing ? (
                              <input
                                type="text"
                                style={inputStyle}
                                value={editableProject.developer || ""}
                                onChange={handleInputChange}
                                name="developer"
                              />
                            ) : (
                              <p>{editableProject.developer}</p>
                            )}
                          </div>
                          <div className="flex-1">
                            <label className="font-medium text-gray-700">
                              Phone Number:
                            </label>
                            {isEditing ? (
                              <input
                                type="text"
                                style={inputStyle}
                                value={editableProject.phoneNumber || ""}
                                onChange={handleInputChange}
                                name="phoneNumber"
                              />
                            ) : (
                              <p>{editableProject.phoneNumber || "N/A"}</p>
                            )}
                          </div>

                        </div>
                        <div className="flex gap-6">
                          <div className="flex-1">
                            <label className="font-medium text-gray-700">
                              WhatsApp Number:
                            </label>
                            {isEditing ? (
                              <input
                                type="text"
                                style={inputStyle}
                                value={editableProject.whatsappNumber || ""}
                                onChange={handleInputChange}
                                name="whatsappNumber"
                              />
                            ) : (
                              <p>{editableProject.whatsappNumber || "N/A"}</p>
                            )}
                          </div>
                          <div className="flex-1" />
                        </div>
                        <div className="flex gap-6">
                          <div className="flex-1">
                            <label className="font-medium text-gray-700">
                              Specifications:
                            </label>
                            {isEditing ? (
                              <textarea
                                type="text"
                                style={inputStyle}
                                value={editableProject.specifications || ""}
                                onChange={handleInputChange}
                                name="specifications"
                              />
                            ) : (
                              <div dangerouslySetInnerHTML={{ __html: editableProject.specifications }} />
                            )}
                          </div>
                          <div className="flex-1">
                            <label className="font-medium text-gray-700">
                              Size:
                            </label>
                            {isEditing ? (
                              <input
                                type="text"
                                style={inputStyle}
                                value={editableProject.sizeAcres || ""}
                                onChange={handleInputChange}
                                name="sizeAcres"
                              />
                            ) : (
                              <p>{editableProject.sizeAcres} acres</p>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-6">
                          <div className="flex-1">
                            <label className="font-medium text-gray-700">
                              Towers:
                            </label>
                            {isEditing ? (
                              <input
                                type="text"
                                style={inputStyle}
                                value={editableProject.towers || ""}
                                onChange={handleInputChange}
                                name="towers"
                              />
                            ) : (
                              <p>{editableProject.towers}</p>
                            )}
                          </div>
                          <div className="flex-1">
                            <label className="font-medium text-gray-700">
                              Units:
                            </label>
                            {isEditing ? (
                              <input
                                type="text"
                                style={inputStyle}
                                value={editableProject.units || ""}
                                onChange={handleInputChange}
                                name="units"
                              />
                            ) : (
                              <p>{editableProject.units}</p>
                            )}
                          </div>

                        </div>
                        {/* Amenities: */}
                        <div className=" mb-4">
                          <label className="font-medium text-gray-700">
                            Amenities:
                          </label>
                        </div>
                        {/* <ul className="list-disc pl-6 text-sm text-gray-600"> */}
                        <div className="flex space-x-3">
                          {isEditing ? (
                            <AmenitiesSelect
                            amenities={editableProject?.amenities}
                            setAmenities={(updatedAmenities) => {
                              setEditableProject((prev) => ({ ...prev, amenities: updatedAmenities }));
                            }}
                            />
                          ) : (
                            project?.amenities?.split(",").map((amenity, index) => (
                              <Chip
                                variant="outline"
                                className="py-1 px-2 rounded-md text-sm"
                                color="black"
                                key={index} // Added key prop here
                              >
                                {pascalToSentenceCase(amenity)}
                              </Chip>
                            ))
                          )}
                          {/* </ul> */}

                        </div>
                      </div>

                    </div>
                  </div>
                </div>

              </>
            ) : (
              <>
                <Skeleton count={5} />

              </>
            )}
          </div>
        </div>
        <ConfirmationModal
          isOpen={isProjectDeleteConfirmationOpen}
          onConfirm={handleConfirmProjectDelete}
          onCancel={handleCloseProjectDeleteConfirmation}
          message={`Are you sure you want to delete project "${project?.projectName}"? This action cannot be undone.`}
        />
       
        {/* Right Sidebar - Phases or Buildings and Configurations */}
        <div className="w-full xl:w-2/5 space-y-2 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100" style={{ overflowY: 'auto', scrollbarWidth: 'thin' }}>
        <PhasesList
            projectPhases={localProjectPhases}
            projectId={projectId}
            onPhaseUpdate={(phaseData) => {
              const p = normalizeProjectPhasePayload(phaseData);
              const id = p?.projectPhaseId ?? p?.project_phase_id;
              if (id == null) return;
              setLocalProjectPhases((prev) => {
                const idx = prev.findIndex(
                  (x) =>
                    String(x.projectPhaseId ?? x.project_phase_id) === String(id)
                );
                if (idx >= 0) {
                  const next = [...prev];
                  next[idx] = { ...next[idx], ...p };
                  return next;
                }
                return [...prev, p];
              });
            }}
          />
        </div>


      </div>

    </>
  );
}

export default ProjectDetailsContent;